import React, { useEffect, useState } from 'react';
import MainLayout from '../../layout/MainLayout';
import { api } from '../../api/axios';
import { tableDateFormate } from '../../utils/dateHelper';
import PaginationBar from '../../components/PaginationBar';
import { FileClock, Filter } from 'lucide-react';

const PAGE_SIZE = 20;

const ACTION_OPTIONS = [
    'USER_DELETE',
    'USER_SUSPEND',
    'USER_ACTIVATE',
    'POST_DELETE',
    'SUBSCRIPTION_GRANT',
    'SUBSCRIPTION_REVOKE',
    'PUSH_SEND',
    'ADMIN_CREATE',
    'ADMIN_DELETE',
    'ADMIN_PASSWORD_RESET',
];

export default function AuditLog() {
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [notFound, setNotFound] = useState(false);

    const [admins, setAdmins] = useState([]);

    // Filters
    const [adminId, setAdminId] = useState('');
    const [action, setAction] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('/admin');
                setAdmins(Array.isArray(res.data) ? res.data : []);
            } catch (_) {
                // non-critical
            }
        })();
    }, []);

    const fetchLogs = async (p = page) => {
        setLoading(true);
        setNotFound(false);
        try {
            const params = new URLSearchParams();
            params.append('page', p);
            params.append('pageSize', PAGE_SIZE);
            if (adminId) params.append('adminId', adminId);
            if (action) params.append('action', action);
            if (from) params.append('from', from);
            if (to) params.append('to', to);

            const res = await api.get(`/audit-logs?${params.toString()}`);
            const data = res.data || {};
            setLogs(data.logs || data.items || []);
            setTotalItems(data.total ?? data.totalItems ?? 0);
            setTotalPages(data.totalPages ?? Math.ceil((data.total || 0) / PAGE_SIZE) ?? 1);
        } catch (err) {
            if (err?.response?.status === 404) {
                setNotFound(true);
                setLogs([]);
            } else {
                console.error('Audit log fetch error:', err);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    const applyFilters = () => {
        setPage(1);
        fetchLogs(1);
    };

    const resetFilters = () => {
        setAdminId('');
        setAction('');
        setFrom('');
        setTo('');
        setPage(1);
        setTimeout(() => fetchLogs(1), 0);
    };

    return (
        <MainLayout title="Audit Log">
            {/* Filters */}
            <div className="bg-white border rounded-2xl shadow-sm p-5 mb-6 flex flex-wrap items-end gap-4">
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">From</label>
                    <input
                        type="date"
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-200 outline-none"
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">To</label>
                    <input
                        type="date"
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-200 outline-none"
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Action</label>
                    <select
                        value={action}
                        onChange={(e) => setAction(e.target.value)}
                        className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-200 outline-none"
                    >
                        <option value="">All actions</option>
                        {ACTION_OPTIONS.map((a) => (
                            <option key={a} value={a}>{a}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Admin</label>
                    <select
                        value={adminId}
                        onChange={(e) => setAdminId(e.target.value)}
                        className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-200 outline-none"
                    >
                        <option value="">All admins</option>
                        {admins.map((a) => (
                            <option key={a._id} value={a._id}>
                                {a.name || a.username}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="ml-auto flex gap-2">
                    <button
                        onClick={resetFilters}
                        className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm"
                    >
                        Reset
                    </button>
                    <button
                        onClick={applyFilters}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                    >
                        <Filter size={14} /> Apply
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white border rounded-2xl shadow-sm overflow-x-auto">
                <table className="w-full text-left">
                    <thead className="bg-gray-50 text-gray-600 uppercase text-xs font-semibold">
                        <tr>
                            <th className="px-6 py-4">Timestamp</th>
                            <th className="px-6 py-4">Admin</th>
                            <th className="px-6 py-4">Action</th>
                            <th className="px-6 py-4">Target Type</th>
                            <th className="px-6 py-4">Target ID</th>
                            <th className="px-6 py-4">Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-10 text-center text-gray-400">
                                    Loading...
                                </td>
                            </tr>
                        ) : notFound ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-10 text-center text-gray-400">
                                    <div className="flex flex-col items-center gap-2">
                                        <FileClock size={32} className="text-gray-300" />
                                        <div className="font-medium">Audit log API not available</div>
                                        <div className="text-xs">The server endpoint <code>GET /api/v1/audit-logs</code> is not implemented yet.</div>
                                    </div>
                                </td>
                            </tr>
                        ) : logs.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-6 py-10 text-center text-gray-400">
                                    No audit logs found for the current filters.
                                </td>
                            </tr>
                        ) : (
                            logs.map((log, idx) => (
                                <tr key={log._id || idx} className="border-b hover:bg-gray-50">
                                    <td className="px-6 py-3 text-sm text-gray-700 whitespace-nowrap">
                                        {log.timestamp || log.createdAt ? tableDateFormate(log.timestamp || log.createdAt) : '-'}
                                    </td>
                                    <td className="px-6 py-3 text-sm">
                                        {log.admin?.name || log.adminName || log.adminId || '-'}
                                    </td>
                                    <td className="px-6 py-3 text-sm">
                                        <span className="px-2 py-1 bg-gray-100 border rounded text-xs font-mono">
                                            {log.action || '-'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3 text-sm">{log.targetType || '-'}</td>
                                    <td className="px-6 py-3 text-xs font-mono text-gray-600">{log.targetId || '-'}</td>
                                    <td className="px-6 py-3 text-xs text-gray-600 max-w-md truncate" title={typeof log.details === 'object' ? JSON.stringify(log.details) : String(log.details ?? '')}>
                                        {typeof log.details === 'object' ? JSON.stringify(log.details) : (log.details ?? '-')}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            <div className="mt-4">
                <PaginationBar
                    page={page}
                    pageSize={PAGE_SIZE}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onChange={setPage}
                />
            </div>
        </MainLayout>
    );
}
